import { useRef } from 'react'
import isNil from 'lodash-es/isNil'

import {
  EMPTY_OBJECT,
  FLOATING_ELEMENT_PLACEMENT,
  INPUT_AUTOCOMPLETE_VALUE,
} from '../../../constants/common'
import { useElementFocusState } from '../../../hooks'
import { stopPropagation } from '../../../utils/events'
import { setRef } from '../../../utils/react'
import { isFormikEmptyValue } from '../../../utils/validation'
import { Counter } from '../../Counter'
import { ICON_HEIGHT_16, IconWithTooltip } from '../../Icon'
import { SymbolCounter } from '../../SymbolCounter'
import { Input } from '../Input'
import { InputTypeValue } from '../Input.constants'
import { InputClearButton } from '../InputClearButton'
import { InputContainer } from '../InputContainer'
import { InputWrap } from '../InputWrap'

import * as styles from './InputField.module.css'

/**
 * @property {boolean} [blocked]
 * blocked: behaves like disabled (no clicks, no value changes),
 * but keeps the normal visual style (no opacity change) and
 * shows a "not-allowed" cursor. Unlike "readonly", it should not
 * handle clicks or focus changes at all.
 *
 * @property {boolean} [disabled]
 * disabled: the control is visually and functionally disabled
 *
 * @property {boolean} [readonly]
 * readonly: value cannot be changed but the control can still receive focus
 */
function InputField({
  ClearButton = InputClearButton,
  Component,
  after,
  afterButton,
  afterIconProps: {
    className: afterIconClassName = styles.afterIcon,
    size: afterIconSize = ICON_HEIGHT_16,
    tooltipPlacement:
      afterIconTooltipPlacement = FLOATING_ELEMENT_PLACEMENT.top,
    ...afterIconProps
  } = EMPTY_OBJECT,
  autoComplete = INPUT_AUTOCOMPLETE_VALUE.on,
  autoFocus,
  before,
  beforeButton,
  beforeIconProps: {
    className: beforeIconClassName = styles.beforeIcon,
    size: beforeIconSize = ICON_HEIGHT_16,
    tooltipPlacement:
      beforeIconTooltipPlacement = FLOATING_ELEMENT_PLACEMENT.top,
    ...beforeIconProps
  } = EMPTY_OBJECT,
  blocked,
  borderless,
  buttonSize,
  className,
  containerRef,
  count,
  disabled,
  errorText,
  forceBlur,
  forceFocus,
  hasCounter,
  height,
  helpText,
  info,
  inline,
  inputId,
  inputProps,
  inputTooltip,
  label,
  link,
  maxLength,
  name,
  placeholder,
  qaName,
  readonly,
  ref,
  required,
  showError,
  type = InputTypeValue.Text,
  value,
  wrapperClassName,
  wrapperRef,
  onBlur,
  onChange,
  onClear,
  onClick,
  onClickBefore,
  onClickContainer,
  onClickInput,
  onFocus,
  onPaste,
}) {
  const inputRef = useRef(null)

  const { focused, handleBlur, handleFocus } = useElementFocusState({
    onBlur,
    onFocus,
  })

  function handleSetInputRef(node) {
    inputRef.current = node

    setRef(ref, node)
  }

  function handleClear(event) {
    stopPropagation(event)

    onClear(event)
  }

  function handleClickContainer(event) {
    stopPropagation(event)

    // #TODO Remove this callback after date picker rework
    // It's crutch for time picker correct work
    onClickContainer?.(inputRef)

    if (!readonly) {
      inputRef?.current?.focus()
    }
  }

  const showWrapCounter =
    !disabled &&
    !blocked &&
    !inline &&
    !borderless &&
    hasCounter &&
    maxLength &&
    (focused || value)

  const showClearButton =
    !disabled &&
    !readonly &&
    !blocked &&
    ClearButton &&
    onClear &&
    !isNil(value) &&
    !isFormikEmptyValue(value)

  return (
    <InputWrap
      blocked={blocked}
      className={wrapperClassName}
      disabled={disabled}
      error={errorText}
      helpText={helpText}
      info={info}
      inputId={inputId || name}
      label={label}
      link={link}
      maxLength={maxLength}
      name={name}
      qaName={qaName}
      ref={wrapperRef}
      required={required}
      tooltip={inputTooltip}
      value={value}
      withCounter={showWrapCounter}
      onClick={onClick}
    >
      <InputContainer
        blocked={blocked}
        borderless={borderless}
        className={className}
        disabled={disabled}
        focused={focused}
        hasError={!!errorText || showError}
        height={height}
        readonly={readonly}
        ref={containerRef}
        onClick={handleClickContainer}
      >
        {beforeIconProps.Icon ? (
          <IconWithTooltip
            {...beforeIconProps}
            className={beforeIconClassName}
            size={beforeIconSize}
            tooltipPlacement={beforeIconTooltipPlacement}
          />
        ) : null}
        {beforeButton}
        {before ? <span onClick={onClickBefore}>{before}</span> : null}
        <Input
          {...inputProps}
          Component={Component}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          blocked={blocked}
          forceBlur={forceBlur}
          forceFocus={forceFocus}
          id={inputId || name}
          maxLength={maxLength}
          name={name}
          placeholder={placeholder}
          readonly={readonly}
          ref={handleSetInputRef}
          type={type}
          value={value}
          onBlur={handleBlur}
          onChange={onChange}
          onClick={onClickInput}
          onFocus={handleFocus}
          onPaste={onPaste}
        />
        {showClearButton ? (
          <ClearButton
            className={styles.clearButton}
            height={buttonSize}
            onClick={handleClear}
          />
        ) : null}
        {(inline || borderless) && focused && hasCounter && maxLength ? (
          <SymbolCounter
            className={styles.counter}
            maxLength={maxLength}
            value={value}
          />
        ) : null}
        {count > 0 ? (
          <Counter className={styles.badgeCounter} count={count} />
        ) : null}
        {afterIconProps.Icon ? (
          <IconWithTooltip
            {...afterIconProps}
            className={afterIconClassName}
            size={afterIconSize}
            tooltipPlacement={afterIconTooltipPlacement}
          />
        ) : null}
        {afterButton}
        {after}
      </InputContainer>
    </InputWrap>
  )
}

export default InputField
